import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { LiturgicalThemeProvider } from "@/contexts/LiturgicalThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import InstallPrompt from "@/components/InstallPrompt";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import MarketplacePage from "@/pages/MarketplacePage";
import ParishPage from "@/pages/ParishPage";
import ParishesPage from "@/pages/ParishesPage";
import MessagesPage from "@/pages/MessagesPage";
import ProfilePage from "@/pages/ProfilePage";
import ParishAdminPage from "@/pages/ParishAdminPage";
import RequestsPage from "@/pages/RequestsPage";
import FriendsAndFamilyPage from "@/pages/FriendsAndFamilyPage";
import SavedPage from "@/pages/SavedPage";
import MyOrdersPage from "@/pages/MyOrdersPage";
import SellerDashboardPage from "@/pages/SellerDashboardPage";
import CartPage from "@/pages/CartPage";
import SacramentFinderPage from "@/pages/SacramentFinderPage";
import JoinParishPage from "@/pages/JoinParishPage";
import DirectoryPage from "@/pages/DirectoryPage";
import BusinessListingDetailPage from "@/pages/BusinessListingDetailPage";
import BusinessEditPage from "@/pages/BusinessEditPage";
import GroupsPage from "@/pages/GroupsPage";
import GroupDetailPage from "@/pages/GroupDetailPage";
import MembersPage from "@/pages/MembersPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import ShopPage from "@/pages/ShopPage";
import MyShopPage from "@/pages/MyShopPage";
import AdminDashboard from "@/pages/AdminDashboard";
import InvitePage from "@/pages/InvitePage";
import SettingsPage from "@/pages/SettingsPage";
import LiturgicalCalendarPage from "@/pages/LiturgicalCalendarPage";
import VaticanNewsPage from "@/pages/VaticanNewsPage";
import EWTNNewsPage from "@/pages/EWTNNewsPage";
import SaintOfTheDayPage from "@/pages/SaintOfTheDayPage";
import RuleOfLifePage from "@/pages/RuleOfLifePage";
import MasstimesImportPage from "@/pages/MasstimesImportPage";
import ClaimParishPage from "@/pages/ClaimParishPage";
import MyRsvpsPage from "@/pages/MyRsvpsPage";
import PostDetailPage from "@/pages/PostDetailPage";
import JobBoardPage from "@/pages/JobBoardPage";
import { DiscoverPeoplePage } from "@/components/FriendSuggestionsWidget";
import AppLayout from "@/components/AppLayout";
import PublicLayout from "@/components/PublicLayout";
import { getMe, processSession, seedData, clearAuthData, persistToken } from "@/lib/api";
import { 
  PrivacyPolicyPage, 
  TermsOfServicePage, 
  CookiePolicyPage, 
  CommunityGuidelinesPage, 
  MarketplaceTermsPage 
} from "@/pages/legal";

function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  useEffect(() => {
    if (hasProcessed.current) return;
    
    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.substring(1)).get("session_id");
    
    if (!sessionId) {
      toast.error("No session ID found. Please try signing in again.");
      navigate("/login");
      return;
    }

    // Mark as processed to prevent re-runs
    hasProcessed.current = true;
    
    // Clear the hash from URL to prevent loops
    window.history.replaceState(null, "", window.location.pathname);

    // Get invite code from sessionStorage (set during registration)
    const inviteCode = sessionStorage.getItem("pending_invite_code");
    
    const attemptProcessSession = async (attempt = 0) => {
      try {
        const res = await processSession(sessionId, inviteCode);
        
        // Clear the pending invite code
        sessionStorage.removeItem("pending_invite_code");
        
        // Store auth data
        persistToken(res.data.token, res.data.user, true);
        
        // Set flag to trigger beta notice on first dashboard visit after login
        sessionStorage.setItem('parish_just_logged_in', 'true');
        
        // Small delay before redirect to ensure storage is persisted (helps on mobile)
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Navigate to dashboard
        window.location.href = "/dashboard";
      } catch (err) {
        console.error(`Session processing failed (attempt ${attempt + 1}):`, err);
        const errorMessage = err.response?.data?.detail || "Session processing failed";
        
        // Retry on "Invalid session" error (could be timing issue on mobile)
        if (errorMessage.includes("Invalid session") && attempt < maxRetries) {
          setRetryCount(attempt + 1);
          // Wait a bit before retry
          await new Promise(resolve => setTimeout(resolve, 500));
          return attemptProcessSession(attempt + 1);
        }
        
        // Show user-friendly error message
        if (errorMessage.includes("Invite code required")) {
          toast.error("New users need an invite code. Please register with an invite link.");
          navigate("/register");
        } else if (errorMessage.includes("Invalid session")) {
          toast.error("Your session has expired. Please try signing in again.");
          navigate("/login");
        } else {
          toast.error(`Sign in failed: ${errorMessage}`);
          navigate("/login");
        }
      }
    };
    
    attemptProcessSession();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#6B665E] font-['Nunito']">
          {retryCount > 0 ? `Signing you in... (retry ${retryCount}/${maxRetries})` : "Signing you in..."}
        </p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // First check if user was passed via navigation state
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }
    
    // Check if we have a stored user/token (localStorage → sessionStorage → cookie fallback)
    const storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || (() => {
      const m = document.cookie.match(/(?:^|; )parish_token=([^;]*)/);
      return m ? m[1] : null;
    })();
    
    if (!token) {
      setLoading(false);
      navigate("/login");
      return;
    }
    
    // If token was found in cookie but not in localStorage, restore it
    if (!localStorage.getItem("token") && !sessionStorage.getItem("token")) {
      localStorage.setItem("token", token);
    }
    
    // Verify the token with the server
    getMe()
      .then((res) => {
        setUser(res.data);
        // Ensure both token and user are persisted in localStorage
        if (!localStorage.getItem("token") && token) {
          localStorage.setItem("token", token);
        }
        localStorage.setItem("user", JSON.stringify(res.data));
        setLoading(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          // Token is truly invalid/expired — clear and redirect
          clearAuthData();
          setLoading(false);
          navigate("/login");
        } else {
          // Network error, timeout, server down — use cached user data
          console.warn("Auth check failed (non-auth error), using cached user:", err.message);
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch {
              clearAuthData();
              navigate("/login");
            }
          } else {
            // No cached user but we have a token — still let them through
            // The API calls will work since the token is in the cookie/localStorage
            setUser({ name: "Loading..." });
          }
          setLoading(false);
        }
      });
  }, [navigate, location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  if (!user) return null;
  return children(user);
}

/**
 * PublicOrAuthRoute - A route that works for both public and authenticated users
 * Shows PublicLayout for guests, AppLayout for authenticated users
 * The page component receives { user, isPublic } props to conditionally render content
 */
function PublicOrAuthRoute({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPublic, setIsPublic] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token") || (() => {
      const m = document.cookie.match(/(?:^|; )parish_token=([^;]*)/);
      return m ? m[1] : null;
    })();
    
    if (!token) {
      // No token - this is a public view
      setIsPublic(true);
      setLoading(false);
      return;
    }
    
    // Restore token to localStorage if only found in cookie
    if (!localStorage.getItem("token") && !sessionStorage.getItem("token")) {
      localStorage.setItem("token", token);
    }
    
    // Have token - try to get user
    getMe()
      .then((res) => {
        setUser(res.data);
        setIsPublic(false);
        setLoading(false);
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          // Token truly invalid — treat as public
          clearAuthData();
          setIsPublic(true);
          setLoading(false);
        } else {
          // Network error — use cached user data if available
          const storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
              setIsPublic(false);
            } catch {
              setIsPublic(true);
            }
          } else {
            setIsPublic(true);
          }
          setLoading(false);
        }
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F7F2]">
        <div className="w-8 h-8 border-2 border-[var(--liturgical-primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isPublic) {
    // Public view - use PublicLayout
    return (
      <PublicLayout>
        {children({ user: null, isPublic: true })}
      </PublicLayout>
    );
  }

  // Authenticated view - use AppLayout
  return (
    <AppLayout user={user}>
      {children({ user, isPublic: false })}
    </AppLayout>
  );
}

function AppRouter() {
  const location = useLocation();

  // Check for session callback using window.location.hash directly
  // This avoids react-router caching issues
  if (window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Legal Pages - Public, no auth required */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
      <Route path="/marketplace-terms" element={<MarketplaceTermsPage />} />
      
      {/* PUBLIC OR AUTHENTICATED ROUTES */}
      {/* These pages work for both guests and logged-in users */}
      <Route
        path="/sacrament-finder"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <SacramentFinderPage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/parishes"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <ParishesPage currentUser={user} isPublic={false} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/parish/:parishId"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <ParishPage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <MarketplacePage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/listing/:listingId"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <ListingDetailPage currentUser={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/directory"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <DirectoryPage currentUser={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/directory/:listingId"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <BusinessListingDetailPage currentUser={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/jobs"
        element={
          <PublicOrAuthRoute>
            {({ user }) => <JobBoardPage user={user} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/vatican-news"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <VaticanNewsPage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/ewtn-news"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <EWTNNewsPage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/saint-of-the-day"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <SaintOfTheDayPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/my-rule-of-life"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <RuleOfLifePage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />

      {/* PROTECTED ROUTES - Members only */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <DashboardPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/post/:postId"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <PostDetailPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/shop/:shopId"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <ShopPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-shop"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MyShopPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/parish"
        element={
          <PublicOrAuthRoute>
            {({ user, isPublic }) => <ParishPage user={user} isPublic={isPublic} />}
          </PublicOrAuthRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MessagesPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:userId"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MessagesPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile/:userId?"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <ProfilePage currentUser={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/discover-people"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <DiscoverPeoplePage />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <SearchResultsPage currentUser={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/parish-admin"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <ParishAdminPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/requests"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <RequestsPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      {/* Legacy route for backward compatibility */}
      <Route
        path="/prayers"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <RequestsPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/saved"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <SavedPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <FriendsAndFamilyPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      {/* Legacy redirects */}
      <Route path="/friends-and-family" element={<ProtectedRoute>{() => <Navigate to="/community" replace />}</ProtectedRoute>} />
      <Route path="/friends" element={<ProtectedRoute>{() => <Navigate to="/community" replace />}</ProtectedRoute>} />
      <Route path="/my-rsvps" element={<ProtectedRoute>{() => <Navigate to="/calendar" replace />}</ProtectedRoute>} />
      <Route path="/family" element={<ProtectedRoute>{() => <Navigate to="/community" replace />}</ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute>{() => <Navigate to="/community?tab=members" replace />}</ProtectedRoute>} />
      <Route
        path="/invite"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <InvitePage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MyOrdersPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <SettingsPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MyRsvpsPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-dashboard"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <SellerDashboardPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <CartPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      {/* Legacy redirect */}
      <Route path="/sacraments" element={<Navigate to="/sacrament-finder" replace />} />
      <Route
        path="/join/:inviteCode"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <JoinParishPage user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/directory/:listingId/edit"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <BusinessEditPage currentUser={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <GroupsPage currentUser={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/groups/:groupId"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <GroupDetailPage currentUser={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <AdminDashboard user={user} />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/import-parishes"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <MasstimesImportPage />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route
        path="/claim-parish"
        element={
          <ProtectedRoute>
            {(user) => (
              <AppLayout user={user}>
                <ClaimParishPage />
              </AppLayout>
            )}
          </ProtectedRoute>
        }
      />
      <Route path="/liturgical-calendar" element={<Navigate to="/calendar" replace />} />
      <Route path="/my-events" element={<Navigate to="/calendar" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    seedData().catch(() => {});
    
    // Register service worker for PWA with update handling
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            
            // Check for updates every 30 minutes (not 5 — avoid reload churn)
            setInterval(() => {
              registration.update();
            }, 30 * 60 * 1000);
          })
          .catch((error) => {
            console.log('SW registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      
        <LiturgicalThemeProvider>
          <div className="paper-texture">
            <BrowserRouter>
              <AppRouter />
              <InstallPrompt />
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
        </LiturgicalThemeProvider>
      
    </ErrorBoundary>
  );
}

export default App;
